// --------------------------------------------------------------
// Created On: 2021-10-05
// Author: Zachary Thomas
//
// Last Modified: 2026-10-02
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useState, useEffect } from "react";
import NavigationDrawerCollection from "./NavigationDrawerCollection/NavigationDrawerCollection";
import NavigationDrawerLink from "./NavigationDrawerLink/NavigationDrawerLink";
import SearchBar from "../SearchBar/SearchBar";
import { useNavigate } from "react-router-dom";
import { RPM_COMPANY, PUBLIC_PAGES } from "../../constants/miscellaneous";
import useReduxSelector from "../../hooks/useReduxSelector";
import userHasPermission from "../../utilities/userHasPermission";
import {
  ALIAS_ATTRIBUTES_PERMISSION,
  CREATE_ASSETS_PERMISSION,
  CREATE_ASSET_GROUPS_PERMISSION,
  CREATE_ASSET_RENTALS_PERMISSION,
  CREATE_REPORTS_PERMISSION,
  DELETE_ASSETS_PERMISSION,
  DELETE_ASSET_GROUPS_PERMISSION,
  REVOKE_ASSET_RENTALS_PERMISSION,
  RETURN_ASSET_RENTALS_PERMISSION,
  DELETE_REPORTS_PERMISSION,
  MANAGE_ALERT_THRESHOLDS_PERMISSION,
  UPDATE_ASSETS_PERMISSION,
  UPDATE_ASSET_GROUPS_PERMISSION,
  UPDATE_ASSET_RENTALS_PERMISSION,
  UPDATE_REPORTS_PERMISSION,
  VIEW_DEVICE_STATUSES_PERMISSION,
  VIEW_SUBSCRIPTIONS_PERMISSION,
  ACCEPT_ASSET_RENTALS_PERMISSION,
  DECLINE_ASSET_RENTALS_PERMISSION,
  TRANSFER_ASSETS_PERMISSION,
  ACCEPT_ASSET_TRANSFERS_PERMISSION,
  DECLINE_ASSET_TRANSFERS_PERMISSION,
  CREATE_TEMPLATES_PERMISSION,
  UPDATE_TEMPLATES_PERMISSION,
  DELETE_TEMPLATES_PERMISSION,
  DIRECT_REGISTER_COMMAND_PERMISSION,
  VIEW_GLOBAL_RPM_REPORTS_PERMISSION,
  CREATE_COMPANY_PERMISSION,
  UPDATE_COMPANY_PERMISSION,
  DELETE_COMPANY_PERMISSION,
  CROSS_COMPANY_PERMISSION,
  MODIFY_ACTIVATION_STATUS_PERMISSION,
  CROSS_SUBSIDIARY_PERMISSION,
  CREATE_SUBSIDIARY_PERMISSION,
  RELEASE_SUBSIDIARY_PERMISSION,
  AGENT_ACCESS_PERMISSION,
} from "../../constants/permissions";
import encodeCompleteUri from "../../utilities/encodeCompleteUri";
import userHasUserPermissions from "../../utilities/userHasUserPermissions";
import userHasRolePermissions from "../../utilities/userHasRolePermissions";
import AiAgentButton from "../AiAgentButton/AiAgentButton";
import styles from "./NavigationDrawer.module.scss";

// Navigation drawer for navigating pages on a mobile device.
export default function NavigationDrawer(props: Props): Component {
  const currentPageIsPublic = PUBLIC_PAGES.includes(props.currentPage);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [inCompany, setInCompany] = useState<boolean>(true);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const navigate = useNavigate();

  // Keep track of the status of the current user for routing purposes.
  useEffect(() => {
    setInCompany(currentUser.companyName !== RPM_COMPANY);
  }, [JSON.stringify(currentUser)]);

  // Clear the search query and navigate to the results page.
  function goToResults(resultsAddress: string): void {
    setSearchQuery("");
    navigate(resultsAddress);
  }

  return (
    <div>
      <div
        className={`${styles.overlay} ${props.showNavigationDrawer ? styles.visible : ""}`}
        onClick={() => props.onCloseNavigationDrawer()}
      />
      <nav className={`${styles.drawer} ${props.showNavigationDrawer ? styles.visible : ""}`} data-test="nav-drawer">
        {props.currentPage === "/notification" && (
          <NavigationDrawerCollection
            name="Notifications"
            icon="bell"
            targetPage="/notification"
            currentPage={props.currentPage}
          >
            <NavigationDrawerLink
              name="Overview"
              targetPage="/notification/overview"
              onClick={() => props.onCloseNavigationDrawer()}
            />
          </NavigationDrawerCollection>
        )}

        {props.currentPage === "/search" && (
          <NavigationDrawerCollection name="Search" icon="search" targetPage="/search" currentPage={props.currentPage}>
            <NavigationDrawerLink name="Assets" targetPage="/search" onClick={() => props.onCloseNavigationDrawer()} />
          </NavigationDrawerCollection>
        )}

        {props.currentPage === "/account" && (
          <NavigationDrawerCollection
            name="Account Settings"
            icon="user"
            targetPage="/account"
            currentPage={props.currentPage}
          >
            <Fragment>
              <NavigationDrawerLink
                name="Account Info"
                targetPage="/account/user-info"
                onClick={() => props.onCloseNavigationDrawer()}
              />
              <NavigationDrawerLink
                name="Change Password"
                targetPage="/account/password"
                onClick={() => props.onCloseNavigationDrawer()}
              />
            </Fragment>
          </NavigationDrawerCollection>
        )}

        {inCompany && (
          <Fragment>
            {!currentUser.isPackager && !currentPageIsPublic && (
              <NavigationDrawerCollection
                name="Data Dashboard"
                icon="dashboard"
                targetPage="/dashboard"
                currentPage={props.currentPage}
              >
                <Fragment>
                  <NavigationDrawerLink
                    name="Asset Map"
                    targetPage="/dashboard/map"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />
                  <NavigationDrawerLink
                    name="Asset List"
                    targetPage="/dashboard/list"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />
                  <NavigationDrawerLink
                    name="Asset Graph"
                    targetPage="/dashboard/graph"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />
                </Fragment>
              </NavigationDrawerCollection>
            )}
          </Fragment>
        )}

        {!inCompany && (
          <NavigationDrawerCollection
            name="RPM Dashboard"
            icon="gear"
            targetPage="/dashboard"
            currentPage={props.currentPage}
          >
            <NavigationDrawerLink
              name="Companies"
              targetPage="/dashboard/companies"
              onClick={() => props.onCloseNavigationDrawer()}
            />
            {userHasPermission([[VIEW_GLOBAL_RPM_REPORTS_PERMISSION]]) && (
              <NavigationDrawerLink
                name="Chatty Assets"
                targetPage="/assets/chatty"
                onClick={() => props.onCloseNavigationDrawer()}
              />
            )}
            {userHasPermission([
              [CREATE_COMPANY_PERMISSION],
              [UPDATE_COMPANY_PERMISSION],
              [DELETE_COMPANY_PERMISSION],
            ]) && (
              <NavigationDrawerLink
                name="Manage Companies"
                targetPage="/manage/companies"
                onClick={() => props.onCloseNavigationDrawer()}
              />
            )}
            {userHasUserPermissions() && (
              <NavigationDrawerLink
                name="Manage Users"
                targetPage="/manage/users"
                onClick={() => props.onCloseNavigationDrawer()}
              />
            )}
            {userHasRolePermissions() && (
              <NavigationDrawerLink
                name="Manage Roles"
                targetPage="/manage/roles"
                onClick={() => props.onCloseNavigationDrawer()}
              />
            )}
            {userHasPermission([[VIEW_GLOBAL_RPM_REPORTS_PERMISSION]]) && (
              <NavigationDrawerLink
                name="Reports"
                targetPage="/reports"
                onClick={() => props.onCloseNavigationDrawer()}
              />
            )}
            <NavigationDrawerLink
              name="Devices"
              targetPage="/device-search"
              onClick={() => props.onCloseNavigationDrawer()}
            />
            {userHasPermission([[CROSS_COMPANY_PERMISSION, UPDATE_ASSETS_PERMISSION]]) && (
              <NavigationDrawerLink
                name="Asset Configuration"
                targetPage="/asset-configuration"
                onClick={() => props.onCloseNavigationDrawer()}
              />
            )}
            {userHasPermission([[CROSS_COMPANY_PERMISSION, MODIFY_ACTIVATION_STATUS_PERMISSION]]) && (
              <NavigationDrawerLink
                name="Activation Status"
                targetPage="/activation-status"
                onClick={() => props.onCloseNavigationDrawer()}
              />
            )}
          </NavigationDrawerCollection>
        )}

        {inCompany && !currentPageIsPublic && (
          <Fragment>
            {props.currentPage === "/asset" && (
              <NavigationDrawerCollection
                name="Asset Details"
                icon="cog"
                targetPage="/asset"
                currentPage={props.currentPage}
              >
                <Fragment>
                  <NavigationDrawerLink
                    name="Data"
                    targetPage="/asset/*/data"
                    onClick={() => props.onCloseNavigationDrawer()}
                    linksToAsset
                  />
                  <NavigationDrawerLink
                    name="Graph"
                    targetPage="/asset/*/graph"
                    onClick={() => props.onCloseNavigationDrawer()}
                    linksToAsset
                  />
                  <NavigationDrawerLink
                    name="Map"
                    targetPage="/asset/*/map"
                    onClick={() => props.onCloseNavigationDrawer()}
                    linksToAsset
                  />
                  <NavigationDrawerLink
                    name="Remote Operation"
                    targetPage="/asset/*/operation"
                    onClick={() => props.onCloseNavigationDrawer()}
                    linksToAsset
                  />
                  {userHasPermission([[DIRECT_REGISTER_COMMAND_PERMISSION]]) && (
                    <NavigationDrawerLink
                      name="Modbus Read/Write"
                      targetPage="/asset/*/modbus"
                      onClick={() => props.onCloseNavigationDrawer()}
                      linksToAsset
                    />
                  )}
                  <NavigationDrawerLink
                    name="Documents"
                    targetPage="/asset/*/documents"
                    onClick={() => props.onCloseNavigationDrawer()}
                    linksToAsset
                  />
                  <NavigationDrawerLink
                    name="Maintenance Logs"
                    targetPage="/asset/*/logs"
                    onClick={() => props.onCloseNavigationDrawer()}
                    linksToAsset
                  />
                  <NavigationDrawerLink
                    name="Alerts"
                    targetPage="/asset/*/alerts"
                    onClick={() => props.onCloseNavigationDrawer()}
                    linksToAsset
                  />
                </Fragment>
              </NavigationDrawerCollection>
            )}

            <NavigationDrawerCollection
              name="Manage Resources"
              icon="cube"
              targetPage="/manage"
              currentPage={props.currentPage}
            >
              <Fragment>
                {userHasPermission([
                  [CREATE_ASSETS_PERMISSION],
                  [UPDATE_ASSETS_PERMISSION],
                  [DELETE_ASSETS_PERMISSION],
                  [CREATE_ASSET_GROUPS_PERMISSION],
                  [UPDATE_ASSET_GROUPS_PERMISSION],
                  [DELETE_ASSET_GROUPS_PERMISSION],
                ]) && (
                  <NavigationDrawerLink
                    name="Assets"
                    targetPage="/manage/assets"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />
                )}

                {userHasPermission([
                  [CREATE_TEMPLATES_PERMISSION],
                  [UPDATE_TEMPLATES_PERMISSION],
                  [DELETE_TEMPLATES_PERMISSION],
                ]) && (
                  <NavigationDrawerLink
                    name="Templates"
                    targetPage="/manage/templates"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />
                )}

                {userHasUserPermissions() && (
                  <NavigationDrawerLink
                    name="Users"
                    targetPage="/manage/users"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />
                )}

                {userHasRolePermissions() && (
                  <NavigationDrawerLink
                    name="Roles"
                    targetPage="/manage/roles"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />
                )}

                {userHasPermission([
                  [CREATE_REPORTS_PERMISSION],
                  [UPDATE_REPORTS_PERMISSION],
                  [DELETE_REPORTS_PERMISSION],
                ]) && (
                  <NavigationDrawerLink
                    name="Reports"
                    targetPage="/manage/reports"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />
                )}

                {userHasPermission([
                  [CREATE_ASSET_RENTALS_PERMISSION],
                  [UPDATE_ASSET_RENTALS_PERMISSION],
                  [REVOKE_ASSET_RENTALS_PERMISSION],
                  [RETURN_ASSET_RENTALS_PERMISSION],
                  [ACCEPT_ASSET_RENTALS_PERMISSION],
                  [DECLINE_ASSET_RENTALS_PERMISSION],
                ]) && (
                  <NavigationDrawerLink
                    name="Rentals"
                    targetPage="/manage/rentals"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />
                )}

                {userHasPermission([
                  [TRANSFER_ASSETS_PERMISSION],
                  [ACCEPT_ASSET_TRANSFERS_PERMISSION],
                  [DECLINE_ASSET_TRANSFERS_PERMISSION],
                ]) && (
                  <NavigationDrawerLink
                    name="Transfers"
                    targetPage="/manage/transfers"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />
                )}

                <NavigationDrawerLink
                  name="Views"
                  targetPage="/manage/views"
                  onClick={() => props.onCloseNavigationDrawer()}
                />

                {userHasPermission([
                  [CROSS_SUBSIDIARY_PERMISSION],
                  [CREATE_SUBSIDIARY_PERMISSION],
                  [RELEASE_SUBSIDIARY_PERMISSION],
                ]) && (
                  <NavigationDrawerLink
                    name="Subsidiaries"
                    targetPage="/manage/subsidiaries"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />
                )}
              </Fragment>
            </NavigationDrawerCollection>

            {userHasPermission([[VIEW_SUBSCRIPTIONS_PERMISSION], [VIEW_DEVICE_STATUSES_PERMISSION]]) && (
              <NavigationDrawerCollection
                name="Monitoring Devices"
                icon="binoculars"
                targetPage="/monitoring-devices"
                currentPage={props.currentPage}
              >
                <Fragment>
                  {userHasPermission([[VIEW_SUBSCRIPTIONS_PERMISSION]]) && (
                    <NavigationDrawerLink
                      name="Subscriptions"
                      targetPage="/monitoring-devices/subscriptions"
                      onClick={() => props.onCloseNavigationDrawer()}
                    />
                  )}

                  {userHasPermission([[VIEW_DEVICE_STATUSES_PERMISSION]]) && (
                    <NavigationDrawerLink
                      name="Statuses"
                      targetPage="/monitoring-devices/statuses"
                      onClick={() => props.onCloseNavigationDrawer()}
                    />
                  )}
                </Fragment>
              </NavigationDrawerCollection>
            )}

            <NavigationDrawerCollection
              name="Data Processing"
              icon="database"
              targetPage="/data-processing"
              currentPage={props.currentPage}
            >
              <Fragment>
                {userHasPermission([[ALIAS_ATTRIBUTES_PERMISSION]]) && (
                  <NavigationDrawerLink
                    name="Attribute Aliases"
                    targetPage="/data-processing/attributes"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />
                )}

                <NavigationDrawerLink
                  name="Attribute Information"
                  targetPage="/data-processing/attribute-info"
                  onClick={() => props.onCloseNavigationDrawer()}
                />

                {userHasPermission([[MANAGE_ALERT_THRESHOLDS_PERMISSION]]) && (
                  <NavigationDrawerLink
                    name="Alert Thresholds"
                    targetPage="/data-processing/alert-thresholds"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />
                )}
              </Fragment>
            </NavigationDrawerCollection>
          </Fragment>
        )}

        {currentPageIsPublic && (
          <Fragment>
            <NavigationDrawerCollection
              name="Account"
              icon="sign-in"
              targetPage={["/login", "/register", "/forgot-password"]}
              currentPage={props.currentPage}
            >
              <Fragment>
                <NavigationDrawerLink
                  name="Login"
                  targetPage="/login"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
                <NavigationDrawerLink
                  name="Create Account"
                  targetPage="/register"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
                <NavigationDrawerLink
                  name="Reset Password"
                  targetPage="/forgot-password"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
              </Fragment>
            </NavigationDrawerCollection>
            <NavigationDrawerCollection
              name="Legal"
              icon="gavel"
              targetPage={["/eula-text", "/eula-viewer", "/privacy-policy-text", "/privacy-policy-viewer"]}
              currentPage={props.currentPage}
            >
              <Fragment>
                <NavigationDrawerLink
                  name="EULA Text"
                  targetPage="/eula-text"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
                <NavigationDrawerLink
                  name="EULA PDF Viewer"
                  targetPage="/eula-viewer"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
                <NavigationDrawerLink
                  name="Privacy Policy Text"
                  targetPage="/privacy-policy-text"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
                <NavigationDrawerLink
                  name="Privacy Policy PDF Viewer"
                  targetPage="/privacy-policy-viewer"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
              </Fragment>
            </NavigationDrawerCollection>
          </Fragment>
        )}

        {currentUser.companyName !== RPM_COMPANY && !currentUser.isPackager && !currentPageIsPublic && (
          <div className="mx-3 my-4">
            <SearchBar
              prompt="Search..."
              location="NAVIGATION_DRAWER"
              searchQuery={searchQuery}
              onChange={(searchQuery) => setSearchQuery(searchQuery)}
              onSubmit={() => goToResults(`/search/${encodeCompleteUri(searchQuery)}`)}
            />
          </div>
        )}

        {inCompany && props.companyHasAgentAccess && userHasPermission([[AGENT_ACCESS_PERMISSION]]) && (
          <div className="mx-3 my-4 text-center">
            <AiAgentButton location="NAVIGATION_DRAWER" onClick={() => props.onOpenAgentChatWindow()} />
          </div>
        )}
      </nav>
    </div>
  );
}

interface Props {
  currentPage: string;
  companyHasAgentAccess: boolean;
  showNavigationDrawer: boolean;
  onCloseNavigationDrawer: () => void;
  onOpenAgentChatWindow: () => void;
}
